import { computed, defineComponent, onMounted, onUnmounted, onUpdated, PropType, ref, watch } from 'vue';
import { Popover } from 'bootstrap';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { CompleteAction, CompleteGetter } from '@/store/complete/complete.module';
import { Project } from '@/store/project/project.model';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { Province } from '@/store/province/province.model';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'CompleteTableComponent',
  components: {
    LoadingComponent,
  },
  props: {
    fiscalYear: {
      type: Object as PropType<FiscalYear>
    },
    province: {
      type: Object as PropType<Province>
    }
  },
  setup: (props) => {
    const router = useRouter();
    const store = useStore();
    const projects = computed<Project[]>(() => {
      const finished = store.getters[CompleteGetter.FINISHED] as Project[];
      return finished.filter(m => m.fiscalYear.id === props.fiscalYear?.id && m.province.id === props.province?.id);
    });
    const isLoading = computed(() => store.getters[CompleteGetter.STATUS] === 'init' || store.getters[CompleteGetter.STATUS] === 'loading');
    const datatable = ref<HTMLTableElement>();
    const dtInstance = ref();
    const popoverList = ref<Popover[]>([]);

    onMounted(() => {
      if (props.fiscalYear && props.province) {
        store.dispatch(CompleteAction.FETCH_FINISHED, { fiscalYearId: props.fiscalYear.id, provinceId: props.province.id });
      }
    });

    onUpdated(() => {
      Promise.resolve(initialPopover())
        .then(() => initialDataTable());
    });

    onUnmounted(() => {
      dtInstance.value?.destroy();
    });

    watch(() => ([props.fiscalYear, props.province]), ([fiscalYear, province]) => {
      if (fiscalYear && province) {
        store.dispatch(CompleteAction.FETCH_FINISHED, { fiscalYearId: fiscalYear.id, provinceId: province.id });
      }
    });

    const selectedClick = (id: number) => {
      router.push(`/project/${id}`);
    };

    const initialDataTable = () => {
      const dt = datatable.value as HTMLTableElement;
      if (!$.fn.dataTable.isDataTable(dt)) {
        dtInstance.value = $(dt).DataTable({
          columnDefs: [
            { type: 'formatted-num', targets: 2 },
            { type: 'formatted-num', targets: 3 },
            { type: 'formatted-num', targets: 4 },
          ],
          language: {
            emptyTable: 'ไม่พบข้อมูล',
            info: 'แสดง _START_ ถึง _END_ จากจำนวนทั้งหมด _TOTAL_',
            infoEmpty: 'แสดง 0 ถึง 0 จากจำนวนทั้งหมด 0',
            infoFiltered: '(ค้นหาจากจำนวนทั้งหมด _MAX_)',
            lengthMenu: 'แสดงข้อมูล _MENU_',
            paginate: {
              next: 'ถัดไป',
              previous: 'ก่อนหน้า',
              first: 'First',
              last: 'Last'
            },
            search: 'ค้นหา:',
            zeroRecords: 'ไม่พบข้อมูลที่ค้นหา',
          }
        });
      }
    };

    const initialPopover = () => {
      const popoverTriggerList = computed<HTMLElement[]>(() => [].slice.call(document.querySelectorAll('[data-bs-toggle="popover-finished"]')) as HTMLElement[]);
      popoverList.value.map(element => element.hide());
      popoverList.value = popoverTriggerList.value?.map(element => new Popover(element, { trigger: 'hover', placement: 'right' }));
    };

    return {
      projects,
      datatable,
      isLoading,
      selectedClick,
    };
  },
});
